<template>
  <div class="login-page">
    <!-- Background decoration -->
    <div class="bg-decoration">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>

    <!-- Main content -->
    <div class="login-content">
      <!-- Logo area -->
      <div class="logo-section">
        <lang-switcher class="lang-switcher-top" />
        <img 
          :src="currentLogo" 
          class="logo-icon" 
          :alt="currentLogoAlt" 
        />
        <h1 class="app-name">{{ currentAppName }}</h1>
        <p class="app-tagline">{{ currentTagline }}</p>
      </div>

      <!-- Login form -->
      <div class="login-form-container">
        <!-- 普通登录表单 -->
        <form class="login-form" @submit.prevent="handleLogin">
          <!-- 用户名输入 -->
          <div class="form-group">
            <label for="username">
              <span class="icon">👤</span>
              {{ $t('loginPage.usernameLabel') }}
            </label>
            <input
              id="username"
              v-model="username"
              type="text"
              :placeholder="$t('loginPage.usernamePlaceholder')"
              autocomplete="username"
              required
              @keypress.enter="handleLogin"
            />
          </div>

          <!-- Password field -->
          <div class="form-group">
            <label for="password">
              <span class="icon">🔒</span>
              {{ $t('loginPage.passwordLabel') }}
            </label>
            <div class="password-input-wrapper">
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                :placeholder="$t('loginPage.passwordPlaceholder')"
                autocomplete="current-password"
                required
                @keypress.enter="handleLogin"
              />
              <button 
                type="button" 
                class="password-toggle"
                @click="showPassword = !showPassword"
                :title="showPassword ? $t('loginPage.hidePassword') : $t('loginPage.showPassword')"
              >
                {{ showPassword ? '🙈' : '👁️' }}
              </button>
            </div>
          </div>

          <!-- Remember me & Forgot password -->
          <div class="form-options">
            <label class="checkbox-label touch-feedback">
              <input 
                type="checkbox" 
                v-model="rememberMe" 
              />
              <span class="custom-checkbox"></span>
              <span>{{ $t('loginPage.rememberMe') }}</span>
            </label>
            
            <a href="#" class="forgot-link" @click.prevent="handleForgotPassword">
              {{ $t('loginPage.forgotPasswordLink') }}
            </a>
          </div>

          <!-- Submit button -->
          <button 
            type="submit" 
            class="btn-submit touch-feedback"
            :class="{ loading: loading }"
            :disabled="loading"
          >
            <span v-if="!loading">{{ $t('loginPage.loginBtn') }}</span>
            <span v-else>
              <span class="spinner"></span>
              {{ $t('loginPage.loginLoading') }}
            </span>
          </button>

          <!-- Register link -->
          <div class="register-link">
            <span>{{ $t('loginPage.noAccount') }}</span>
            <router-link :to="registerLink" class="register-link-text">{{ $t('loginPage.registerNow') }}</router-link>
          </div>

          <!-- Google 登录（所有环境） -->
          <div class="google-login-section">
            <button type="button" class="google-login-btn" @click="handleGoogleLoginUniversal" :disabled="loading">
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
              </svg>
              <span>{{ $t('loginPage.googleLogin') || 'Google 登录' }}</span>
            </button>
          </div>

          <!-- Apple 登录（所有环境） -->
          <div class="apple-login-section">
            <button type="button" class="apple-login-btn" @click="handleAppleLoginUniversal" :disabled="loading">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <span>{{ $t('loginPage.appleLogin') || 'Apple 登录' }}</span>
            </button>
          </div>

          <!-- APP 下载 - 仅在非 APP 环境显示 -->
          <div class="app-download-section" v-if="!isAppEnvironment">
            <a :href="apkDownloadUrl" class="app-download-link android-download-link" download>
              🤖 {{ $t('loginPage.downloadApp') }} (Android)
            </a>
            <a href="https://apps.apple.com/cn/app/wholesale/id6809011555" target="_blank" class="app-download-link ios-download-link">
              🍎 {{ $t('loginPage.downloadApp') }} (iOS)
            </a>
          </div>

          <!-- WhatsApp 联系我们 -->
          <div class="whatsapp-contact" v-if="whatsappLink">
            <a :href="whatsappLink" target="_blank" class="whatsapp-link">
              {{ $t('loginPage.whatsappContact') }}
            </a>
          </div>

          <!-- 条款链接 -->
          <div class="terms-links">
            <p>{{ $t('Login.line157_text') }} <a href="#" @click.prevent="showTerms('privacy')">{{ $t('loginPage.privacyPolicy') }}</a> | <a href="#" @click.prevent="showTerms('service')">{{ $t('loginPage.termsOfService') }}</a></p>
          </div>
        </form>


      </div>
    </div>

    <!-- Terms modal -->
    <div v-if="showTermsModal" class="modal-overlay" @click.self="showTermsModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ currentTermsTitle }}</h3>
          <button class="close-btn" @click="showTermsModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-html="currentTermsContent"></div>
        </div>
      </div>
    </div>

    <!-- Toast notification -->
    <div v-if="toast.show" :class="['toast', `toast-${toast.type}`, { show: toast.show }]">
      {{ toast.message }}
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import LangSwitcher from '@/components/LangSwitcher.vue';
import api from '@/services/api';

export default {
  name: 'Login',
  components: { LangSwitcher },
  data() {
    return {
      loginType: 'username', // 'username' or 'phone'
      username: '',
      phone: '',
      password: '',
      rememberMe: false,
      showPassword: false,
      loading: false,
      wholesalerInfo: null, // 批发商信息（包含头像）
      apkDownloadUrl: '', // 从后台配置读取的 APK 下载地址
      toast: {
        show: false,
        message: '',
        type: 'info'
      },
      showTermsModal: false,
      currentTermsType: 'service',
      termsContent: {
        service: {
          title: '服务条款',
          content: `
            <h4>欢迎使用 Naporlun 批发系统</h4>
            <p>请您仔细阅读本服务条款，使用本系统即表示您同意遵守以下条款：</p>
            <h5>1. 服务说明</h5>
            <p>Naporlun 批发系统为批发商和档口客户提供商品管理、订单处理、库存管理等服务。</p>
            <h5>2. 账号责任</h5>
            <p>• 您应对账号下的所有活动负责<br/>• 不得将账号转让、出借给他人<br/>• 发现账号被盗用应立即通知我们</p>
            <h5>3. 使用规范</h5>
            <p>• 不得利用系统从事违法活动<br/>• 不得干扰或破坏系统正常运行<br/>• 不得未经授权访问他人数据</p>
            <h5>4. 免责声明</h5>
            <p>因不可抗力、系统维护或网络故障导致的服务中断，我们不承担责任。</p>
          `
        },
        privacy: {
          title: '隐私政策',
          content: '<h4>我们如何收集和使用您的信息</h4><h5>1. 信息收集</h5><p>我们会收集以下信息：</p><p>注册信息：用户名、邮箱、店铺名称</p><p>交易信息：订单记录、支付信息</p><p>设备信息：设备型号、操作系统、IP地址</p><p>使用信息：登录时间、操作记录</p><h5>2. 信息使用</h5><p>您的信息用于：</p><p>提供和改进服务、处理订单和支付、发送服务通知、保障账号安全</p><h5>3. 信息保护</h5><p>采用加密技术保护数据传输，严格限制员工访问权限，定期安全审计，不会向第三方出售您的信息</p><h5>4. 您的权利</h5><p>查看和修改个人信息、删除账号及相关数据、撤回授权同意</p>'
        }
      }
    };
  },
  computed: {
    currentLogo() {
      // 优先使用批发商头像，否则使用默认logo
      return this.wholesalerInfo?.avatarUrl || '/naporlun-logo.svg';
    },
    currentLogoAlt() {
      return this.wholesalerInfo?.companyName || 'NAPORLUN';
    },
    currentAppName() {
      return this.wholesalerInfo?.companyName || 'NAPORLUN';
    },
    currentTagline() {
      return this.wholesalerInfo?.companyName
        ? this.$t('loginPage.appTagline')
        : this.$t('loginPage.appTagline');
    },
    currentTermsTitle() {
      return this.termsContent[this.currentTermsType]?.title || '';
    },
    currentTermsContent() {
      return this.termsContent[this.currentTermsType]?.content || '';
    },
    ...mapGetters('auth', ['isLoggedIn']),
    isAppEnvironment() {
      return typeof window !== 'undefined' && window.Capacitor;
    },
    isIOS() {
      if (!this.isAppEnvironment) return false;
      const platform = window.Capacitor?.getPlatform?.();
      return platform === 'ios';
    },
    isAndroidApp() {
      if (!this.isAppEnvironment) return false;
      const platform = window.Capacitor?.getPlatform?.();
      return platform === 'android';
    },
    whatsappLink() {
      if (this.wholesalerInfo && this.wholesalerInfo.whatsappNumber) {
        const phone = this.wholesalerInfo.whatsappNumber.replace(/[^\d+]/g, '');
        return `https://wa.me/${phone}`;
      }
      return 'https://wa.me/6586031798';
    },
    registerLink() {
      const query = {}
      if (this.$route.query.shareCode) query.shareCode = this.$route.query.shareCode
      if (this.$route.query.redirect) query.redirect = this.$route.query.redirect
      if (this.$route.query.wholesalerId) query.wholesalerId = this.$route.query.wholesalerId
      return Object.keys(query).length ? { path: '/register', query } : '/register'
    },
  },
  methods: {
    ...mapActions('auth', ['login']),
    // 从后台配置读取 APK 下载地址
    async loadApkDownloadUrl() {
      try {
        const response = await api.get('/app-version/check', {
          params: { platform: 'ANDROID', currentVersion: 0 }
        });
        if (response?.hasUpdate && response?.downloadUrl) {
          this.apkDownloadUrl = response.downloadUrl;
        }
      } catch (error) {
        console.error('获取 APK 下载地址失败:', error);
      }
    },

    // 统一处理 Google 登录（网页用 SDK，APP 用原生插件）
    async handleGoogleLoginUniversal() {
      if (this.isAppEnvironment) {
        // APP 环境：使用原生 Google 登录
        await this.handleNativeGoogleLogin();
      } else {
        // 网页环境：使用 Google SDK
        await this.handleGoogleLogin();
      }
    },

    // 统一处理 Apple 登录
    async handleAppleLoginUniversal() {
      if (this.isAppEnvironment && this.isIOS) {
        // iOS APP：使用原生 Apple 登录
        await this.handleAppleLogin();
      } else if (this.isAppEnvironment && this.isAndroidApp) {
        // Android APP：提示不支持
        this.showToast('Apple 登录仅在 iOS 设备上可用', 'info');
      } else {
        // 网页环境：使用 Apple JS SDK
        await this.handleWebAppleLogin();
      }
    },

    // 网页版 Apple 登录（后端 OAuth 方式）
    async handleWebAppleLogin() {
      try {
        this.loading = true;
        
        // 构建 Apple 授权 URL
        const clientId = 'com.naporlun.web';
        const redirectUri = encodeURIComponent(window.location.origin + '/api/auth/apple-callback');
        const state = 'wholesale-login_' + Date.now();
        
        // 保存 state 到 sessionStorage，用于回调验证
        sessionStorage.setItem('apple_login_state', state);
        
        // Apple 授权 URL
        const appleAuthUrl = `https://appleid.apple.com/auth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=name+email&state=${state}&response_mode=form_post`;
        
        // 跳转到 Apple 授权页面
        window.location.href = appleAuthUrl;
        
      } catch (error) {
        console.error('Apple login redirect error:', error);
        this.showToast('Apple 登录跳转失败，请重试', 'error');
        this.loading = false;
      }
    },

    // ⭐ 公共登录成功逻辑（普通登录、Apple 登录、Google 登录共用）
    async handleLoginSuccess(response) {
      // ✅ 存储 token
      if (response.token) {
        localStorage.setItem('auth_token', response.token);
      }
      
      // ⭐ 保存 URL 中的 redirect（角色选择完成后使用）
      const urlRedirect = this.$route.query.redirect;
      if (urlRedirect) {
        sessionStorage.setItem('login_redirect', urlRedirect);
      }
      
      // ⭐ 保存 URL 中的批发商ID（档口登录后用于关联）
      const urlWholesalerId = this.$route.query.wholesalerId || sessionStorage.getItem('url_wholesaler_id');
      if (urlWholesalerId) {
        sessionStorage.setItem('url_wholesaler_id', urlWholesalerId);
        console.log('✅ 保存 URL 批发商ID:', urlWholesalerId);
      }
      
      // ⭐ 根据角色数量决定是否跳转角色选择页
      const roleOptions = response.roleOptions || [];
      console.log('📦 所有角色:', roleOptions);
      
      // ⭐ 先检查是否需要选择商户（批发商角色）
      if (response.requiresWholesalerSelection) {
        console.log('🏪 需要选择商户，跳转到商户选择页');
        const roleCode = roleOptions.length === 1 ? roleOptions[0].roleCode : 'WHOLESALER';
        const redirectPath = roleOptions.length === 1 ? roleOptions[0].redirectPath : '/wholesaler/home';
        
        sessionStorage.setItem('tempToken', response.token);
        sessionStorage.setItem('tempRoleCode', roleCode);
        sessionStorage.setItem('tempRedirectPath', redirectPath);
        sessionStorage.setItem('needsWholesalerSelect', 'true');
        // 保存批发商列表
        if (response.wholesalerOptions) {
          sessionStorage.setItem('wholesalerOptions', JSON.stringify(response.wholesalerOptions));
        }
        this.$router.replace({ path: '/wholesaler-select' });
        return;
      }
      
      // 如果有多个角色，跳转到角色选择页
      if (roleOptions.length > 1) {
        console.log('🎭 多个角色，跳转到角色选择页');
        console.log('🔍 Capacitor 环境:', !!window.Capacitor);
        console.log('🔍 roleOptions:', roleOptions);
        sessionStorage.setItem('tempToken', response.token);  // 保存 token
        sessionStorage.setItem('needsRoleSelect', 'true');
        // ⭐ 保存角色选项（Apple/Google 登录没有密码，无法重新调 /auth/login）
        sessionStorage.setItem('roleOptions', JSON.stringify(roleOptions));
        if (response.userId) sessionStorage.setItem('tempUserId', response.userId.toString());
        // ⭐ 保存 redirect，角色选择完成后跳转
        if (urlRedirect) {
          sessionStorage.setItem('login_redirect', urlRedirect);
        }
        // 保存批发商列表和权限
        if (response.wholesalerOptions) {
          sessionStorage.setItem('wholesalerOptions', JSON.stringify(response.wholesalerOptions));
        }
        if (response.features) {
          sessionStorage.setItem('userFeatures', JSON.stringify(Array.from(response.features)));
        }
        // ⭐ 保存档口 ID（多角色场景）
        if (response.customerId) {
          sessionStorage.setItem('customerId', response.customerId.toString());
        }
        // ⭐ 保存 URL 批发商ID（角色选择后用于关联）
        if (urlWholesalerId) {
          sessionStorage.setItem('url_wholesaler_id', urlWholesalerId);
        }
        this.$router.replace({ path: '/role-select' });
        return;
      }
      
      // 只有一个角色，直接登录
      if (roleOptions.length === 1) {
        const roleCode = roleOptions[0].roleCode;
        let redirectPath = roleOptions[0].redirectPath;
        
        // 根据角色设置正确的首页
        if (roleCode === 'DRIVER') {
          redirectPath = '/delivery';  // 配送员首页
          // 后端已直接返回 driverId
          if (response.driverId) {
            localStorage.setItem('driver_id', response.driverId.toString());
            if (response.userId) localStorage.setItem('user_id', response.userId.toString());
            const driverInfo = {
              id: response.driverId,
              name: response.username || '配送员',
              phone: response.username,
              wholesalerId: response.wholesalerId || null,
              status: 'IDLE',
              acceptContractorOrders: false
            };
            localStorage.setItem('driver_info', JSON.stringify(driverInfo));
            console.log('✅ 保存配送员信息:', driverInfo);
          }
        }
        
        // 保存角色
        localStorage.setItem('user_identity', roleCode);
        console.log('✅ 设置角色为:', roleCode);
        
        // 保存商户 ID（后端返回的 wholesalerId 或从 role 获取）
        if (response.wholesalerId) {
          localStorage.setItem('stored_wholesaler_id', response.wholesalerId.toString());
          console.log('✅ 保存商户 ID:', response.wholesalerId);
        }
        
        // ⭐ 保存档口 ID（后端返回的 customerId）
        if (response.customerId) {
          localStorage.setItem('stored_customer_id', response.customerId.toString());
          console.log('✅ 保存档口 ID:', response.customerId);
        }
        
        // 保存权限
        if (response.features) {
          localStorage.setItem('user_features', JSON.stringify(Array.from(response.features)));
        }
        
        // 跳转 - 优先使用 URL 中的 redirect 参数
        if (urlRedirect) {
          console.log('🚀 跳转到 URL redirect:', urlRedirect);
          this.$router.replace({ path: urlRedirect });
          return;
        }
        if (redirectPath) {
          console.log('🚀 跳转到角色默认页:', redirectPath);
          this.$router.replace({ path: redirectPath });
          return;
        }
      }
      
      // 默认跳转
      this.$router.replace({ path: '/products' });
    },

    async handleLogin() {
      if (!this.username || !this.password) {
        this.showToast(this.$t('loginPage.pleaseInputUsernamePassword'), 'error');
        return;
      }

      this.loading = true;
      try {
        // 登录前清除所有旧的 token 和身份信息（参考 logout 方法）
        const keys = [
          'token',
          'authToken',
          'auth_token',
          'access_token',
          'Authorization',
          'user_identity',
          'identity',
          'identityId',
          'userId',
          'user_id',
          'userInfo',
          'user_features',
          'user_role',
          'stored_wholesaler_id',
          'stored_customer_id',
          'wholesaler_id',
          'customer_id',
          'driver_id'
        ];
        keys.forEach(k => {
          localStorage.removeItem(k);
          sessionStorage.removeItem(k);
        });
        sessionStorage.removeItem('tempUsername');
        sessionStorage.removeItem('tempPassword');
        sessionStorage.removeItem('needsIdentitySelect');
        sessionStorage.removeItem('needsRoleSelect');
        sessionStorage.removeItem('roleOptions');
        sessionStorage.removeItem('tempToken');

        // ⭐ 从 URL 获取批发商ID（档口登录后用于关联）
        const urlWholesalerId = this.$route.query.wholesalerId;

        const response = await this.login({
          username: this.username,
          password: this.password,
          wholesalerId: urlWholesalerId ? parseInt(urlWholesalerId) : undefined
        });

        this.showToast(this.$t('loginPage.loginSuccess'), 'success');
        
        // ⭐ 复用公共登录成功逻辑
        await this.handleLoginSuccess(response);
      } catch (error) {
        // ✅ 【核心修复】登录失败时：绝不跳转，只提示 + 清理状态
        this.showToast(
          error?.message || 
          error?.response?.data?.message || 
          this.$t('loginPage.loginFailed'), 
          'error'
        );

        // ✅ 强制清理本地凭证（防 401 循环）
        localStorage.removeItem('auth_token');
        sessionStorage.removeItem('tempUsername');
        sessionStorage.removeItem('tempPassword');
        sessionStorage.removeItem('needsIdentitySelect');
      } finally { 
        this.loading = false;
      }
    },

    async handlePhoneLogin() {
      if (!this.phone || !this.password) {
        this.showToast(this.$t('loginPage.pleaseInputPhonePassword'), 'error');
        return;
      }

      if (!/^[0-9]{11}$/.test(this.phone)) {
        this.showToast(this.$t('loginPage.invalidPhone'), 'error');
        return;
      }

      this.loading = true;
      try {
        // 手机号登录暂时使用相同的 API，后端需要支持手机号识别
        const response = await this.login({
          username: this.phone,
          password: this.password
        });

        if (response.requiresIdentitySelection) {
          // 存储临时凭证到 sessionStorage
          sessionStorage.setItem('tempUsername', this.phone);
          sessionStorage.setItem('tempPassword', this.password);
          sessionStorage.setItem('needsIdentitySelect', 'true');
          
          // 跳转到身份选择页面
          this.$router.push('/identity-select');
          this.showToast(this.$t('loginPage.selectIdentityHint'), 'info');
          return;
        }

        this.showToast(this.$t('loginPage.loginSuccess'), 'success');
        
        // 清除旧的身份信息
        localStorage.removeItem('identity');
        
        // 存储 token（如果记住我）
        if (this.rememberMe) {
          localStorage.setItem('rememberedUsername', this.username);
        }

        // 根据身份跳转到不同页面
        const redirect = this.$route.query.redirect;
        let targetPath;
        
        if (redirect) {
          // 有指定跳转地址，使用指定的
          targetPath = redirect;
        } else {
          // 根据身份决定默认跳转页面
          const identity = response.identity;
          // 存储新的身份
          localStorage.setItem('identity', identity);
          
          switch (identity) {
            case 'ADMIN':
              targetPath = '/admin/dashboard';  // 管理后台
              break;
            case 'CUSTOMER':
              targetPath = '/customer/home';    // 档口首页
              break;
            case 'WHOLESALER':
              targetPath = '/wholesaler/home';  // 批发商首页
              break;
            default:
              targetPath = '/404';              // 未知角色，跳转 404
          }
        }
        
        setTimeout(() => {
          this.$router.push(targetPath);
        }, 500);
      } catch (error) {
        this.showToast(error.message || this.$t('loginPage.loginFailed'), 'error');
      } finally {
        this.loading = false;
      }
    },

    // 身份图标映射（用于显示）
    getIdentityIcon(identityType) {
      const icons = {
        CUSTOMER: '🏪',
        WHOLESALER: '📦',
        ADMIN: '⚙️'
      };
      return icons[identityType] || '👤';
    },

    handleSmsLogin() {
      this.showToast(this.$t('loginPage.smsLoginComing'), 'info');
    },

    async handleGoogleLogin() {
      try {
        this.loading = true;
        
        // 动态加载 Google 登录 SDK
        if (!window.google) {
          await this.loadGoogleScript();
        }

        // 初始化 Google 登录
        window.google.accounts.id.initialize({
          client_id: '217255898087-lf1esgjrpalvud1biurj7q5cotm9am9a.apps.googleusercontent.com',
          callback: this.handleGoogleCredentialResponse
        });

        // 显示 Google 登录弹窗
        window.google.accounts.id.prompt();

      } catch (error) {
        console.error('Google login error:', error);
        this.showToast('Google 登录失败：' + error.message, 'error');
      } finally {
        this.loading = false;
      }
    },

    // APP 环境下的原生 Google 登录
    async handleNativeGoogleLogin() {
      try {
        this.loading = true;
        
        // 检查是否安装了插件
        if (!window.Capacitor?.Plugins?.GoogleAuth) {
          this.showToast('Google 登录插件未安装', 'error');
          return;
        }

        const GoogleAuth = window.Capacitor.Plugins.GoogleAuth;
        
        // 初始化插件
        await GoogleAuth.initialize({
          clientId: '217255898087-lf1esgjrpalvud1biurj7q5cotm9am9a.apps.googleusercontent.com',
          scopes: ['profile', 'email'],
          grantOfflineAccess: true
        });

        // 执行登录
        const result = await GoogleAuth.signIn();
        
        if (result && result.authentication && result.authentication.idToken) {
          // 调用后端验证
          await this.handleGoogleCredentialResponse({
            credential: result.authentication.idToken
          });
        } else {
          throw new Error('未获取到登录凭证');
        }

      } catch (error) {
        console.error('Native Google login error:', error);
        this.showToast('Google 登录失败：' + (error.message || '未知错误'), 'error');
        this.loading = false;
      }
    },

    // Apple 登录（仅 iOS APP 环境）
    async handleAppleLogin() {
      try {
        this.loading = true;
        
        // 检查是否安装了插件
        if (!window.Capacitor?.Plugins?.SignInWithApple) {
          this.showToast('Apple 登录插件未安装', 'error');
          return;
        }

        const SignInWithApple = window.Capacitor.Plugins.SignInWithApple;
        
        // 执行 Apple 登录
        const result = await SignInWithApple.authorize({
          scopes: 'email name',
          state: '12345'
        });
        
        if (result && result.response && result.response.identityToken) {
          const identityToken = result.response.identityToken;
          
          // 尝试从 Apple 获取姓名（Apple 只在首次授权时返回）
          let appleFullName = '';
          if (result.response.fullName) {
            const { givenName = '', familyName = '' } = result.response.fullName;
            appleFullName = `${givenName} ${familyName}`.trim();
          }
          
          // 调用后端接口验证 token，传递 fullName 以便后端保存
          const res = await api.post('/auth/apple-login', { 
            identityToken,
            fullName: appleFullName
          });
          
          // 检查用户是否已有 customerName
          if (res && res.token) {
            // 保存 token
            localStorage.setItem('auth_token', res.token);
            
            // 如果 customerName 为空，弹窗让用户输入
            if (!res.customerName) {
              let fullName = '';
              try {
                const { value } = await this.$prompt('请输入您的姓名', '完善信息', {
                  confirmButtonText: '确定',
                  cancelButtonText: '取消',
                  inputPattern: /\S+/,
                  inputErrorMessage: '姓名不能为空'
                });
                fullName = value;
              } catch (e) {
                // 用户取消输入
                this.loading = false;
                return;
              }
              
              // 更新用户姓名到后端
              await api.put('/auth/update-name', { customerName: fullName });
              res.customerName = fullName;
            }
            
            // 继续处理登录成功后的逻辑
            await this.handleAppleCredentialResponse({
              identityToken,
              fullName: res.customerName,
              preAuth: res
            });
          }
        } else {
          throw new Error('未获取到登录凭证');
        }

      } catch (error) {
        console.error('Apple login error:', error);
        this.showToast('Apple 登录失败：' + (error.message || '未知错误'), 'error');
        this.loading = false;
      }
    },

    // 处理 Apple 登录回调
    async handleAppleCredentialResponse(response) {
      try {
        this.loading = true;
        
        let res;
        
        // 如果有预认证数据（来自 handleAppleLogin），直接使用
        if (response.preAuth) {
          res = response.preAuth;
        } else {
          // 否则重新调用后端接口验证
          const loginPayload = {
            identityToken: response.identityToken,
            fullName: response.fullName || ''
          };
          
          // 批发商ID：优先从路由参数，其次从 query 参数
          const urlWholesalerId = this.$route.params.wholesalerId || this.$route.query.wholesalerId;
          if (urlWholesalerId) {
            loginPayload.wholesalerId = urlWholesalerId;
          }
          
          // 分享码：从 URL query 参数获取
          if (this.$route.query.shareCode) {
            loginPayload.shareCode = this.$route.query.shareCode;
          }
          
          // 调用后端接口验证 Apple Token
          res = await api.post('/auth/apple-login', loginPayload);
        }

        if (res && res.token) {
          // ⭐ 复用普通登录的完整逻辑
          await this.handleLoginSuccess(res);
        } else {
          throw new Error('登录失败：未返回 token');
        }

      } catch (error) {
        console.error('Apple login callback error:', error);
        this.showToast('Apple 登录失败：' + (error.response?.data?.message || error.message), 'error');
        this.loading = false;
      }
    },

    // 加载 Google 登录脚本
    loadGoogleScript() {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    // 处理 Google 登录回调
    async handleGoogleCredentialResponse(response) {
      try {
        this.loading = true;
        
        // ⭐ 收集子域名和分享码（用于自动关联批发商）
        const loginPayload = {
          idToken: response.credential
        };
        
        // 批发商ID：优先从路由参数，其次从 query 参数
        const urlWholesalerId = this.$route.params.wholesalerId || this.$route.query.wholesalerId;
        if (urlWholesalerId) {
          loginPayload.wholesalerId = urlWholesalerId;
          sessionStorage.setItem('url_wholesaler_id', urlWholesalerId);
          console.log('✅ Google 登录保存 URL 批发商ID:', urlWholesalerId);
        }
        
        // 分享码：从 URL query 参数获取
        if (this.$route.query.shareCode) {
          loginPayload.shareCode = this.$route.query.shareCode;
        }
        
        // 调用后端接口验证 Google Token
        const res = await api.post('/auth/google-login', loginPayload);

        if (res && res.token) {
          // 保存 token
          localStorage.setItem('auth_token', res.token);
          
          // 保存用户信息（跟普通登录一样）
          if (res.userId) {
            localStorage.setItem('user_id', res.userId.toString());
          }
          
          if (res.features) {
            localStorage.setItem('user_features', JSON.stringify(Array.from(res.features)));
          }
          
          // 处理角色选择（跟普通登录一样）
          const roleOptions = res.roleOptions || [];
          
          if (roleOptions.length > 1) {
            // 多角色，需要选择
            sessionStorage.setItem('tempToken', res.token);
            sessionStorage.setItem('needsRoleSelect', 'true');
            sessionStorage.setItem('roleOptions', JSON.stringify(roleOptions));
            
            this.showToast('请选择角色', 'info');
            setTimeout(() => {
              this.$router.push('/role-select');
            }, 500);
          } else if (roleOptions.length === 1) {
            // 单角色，直接跳转
            const role = roleOptions[0];
            localStorage.setItem('user_role', role.roleCode);
            
            // 保存角色特定信息
            if (role.roleCode === 'WHOLESALER' && res.wholesalerId) {
              localStorage.setItem('wholesaler_id', res.wholesalerId.toString());
              localStorage.setItem('stored_wholesaler_id', res.wholesalerId.toString());
            } else if ((role.roleCode === 'CUSTOMER' || role.roleCode === 'STALL') && res.customerId) {
              localStorage.setItem('customer_id', res.customerId.toString());
              localStorage.setItem('stored_customer_id', res.customerId.toString());
            } else if (role.roleCode === 'DRIVER' && res.driverId) {
              localStorage.setItem('driver_id', res.driverId.toString());
            }
            
            this.showToast('登录成功', 'success');
            setTimeout(() => {
              this.$router.push(role.redirectPath || '/404');
            }, 500);
          } else {
            // 无角色，跳转 404
            this.showToast('登录成功', 'success');
            setTimeout(() => {
              this.$router.push('/404');
            }, 500);
          }
        } else {
          throw new Error('登录失败：未返回 token');
        }

      } catch (error) {
        console.error('Google login callback error:', error);
        this.showToast('Google 登录失败：' + (error.response?.data?.message || error.message), 'error');
        this.loading = false;
      }
    },

    handleForgotPassword() {
      this.showToast(this.$t('loginPage.resetPasswordHint'), 'info');
    },

    toggleRememberMe() {
      this.rememberMe = !this.rememberMe;
    },

    showToast(message, type = 'info') {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;
      
      setTimeout(() => {
        this.toast.show = false;
      }, 3000);
    },

    showTerms(type) {
      this.currentTermsType = type;
      this.showTermsModal = true;
    },

    handleIosDownload() {
      window.open('https://apps.apple.com/cn/app/wholesale/id6809011555', '_blank');
    },

    loadRememberedAccount() {
      const rememberedUsername = localStorage.getItem('rememberedUsername');
      
      if (rememberedUsername) {
        this.username = rememberedUsername;
        this.rememberMe = true;
      }
    },

    async loadWholesalerInfo() {
      // 通过路由参数或 query 参数获取批发商ID
      // 支持 /wholesale/:wholesalerId 和 /login?wholesalerId=xxx
      const wholesalerId = this.$route.params.wholesalerId || this.$route.query.wholesalerId;
      if (wholesalerId) {
        try {
          const res = await api.get(`/public/wholesale/${wholesalerId}`);
          if (res) {
            this.wholesalerInfo = res;
          }
        } catch (error) {
          console.log('未找到批发商信息:', error);
        }
      }
    }
  },
  mounted() {
    if (this.isLoggedIn) {
      this.$router.push('/404');
      return;
    }
    this.loadRememberedAccount();
    this.loadWholesalerInfo();
    this.loadApkDownloadUrl();
  }
};
</script>

<style lang="scss" scoped>
.login-page {
  min-height: 100vh;
  position: relative;
  overflow: visible;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  padding-bottom: 80px; /* 为底部 footer 留空间 */
}

.login-content {
  width: 100%;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 30px 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  animation: slideUp 0.5s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 移动端优化
@media (max-width: 768px) {
  .login-page {
    padding: 15px;
  }
  
  .login-content {
    padding: 25px 20px;
    border-radius: 16px;
  }
  
  .logo-section {
    .logo-icon {
      width: 56px !important;
      height: 56px !important;
      font-size: 56px !important;
    }
    
    .app-name {
      font-size: 22px !important;
    }
    
    .app-tagline {
      font-size: 13px !important;
    }
  }
}

// Background decorations
.bg-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
  
  .circle {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    animation: float 20s infinite ease-in-out;
  }
  
  .circle-1 {
    width: 200px;
    height: 200px;
    top: -50px;
    left: -50px;
    animation-delay: 0s;
  }
  
  .circle-2 {
    width: 150px;
    height: 150px;
    bottom: 100px;
    right: -30px;
    animation-delay: -5s;
  }
  
  .circle-3 {
    width: 100px;
    height: 100px;
    top: 50%;
    right: 20%;
    animation-delay: -10s;
  }
}

@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-30px) rotate(180deg); }
}

// Main content
.login-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 40px 20px;
  max-width: 480px;
  margin: 0 auto;
}

// Logo section
.logo-section {
  text-align: center;
  margin-bottom: 40px;
  position: relative;
  
  .lang-switcher-top {
    position: absolute;
    top: env(safe-area-inset-top, 0px);
    right: 0;
    z-index: 10;
  }
  
  .logo-icon {
    width: 60px;
    height: 60px;
    font-size: 60px;
    margin-bottom: 16px;
    animation: pulse 2s infinite;
  }
  
  .app-name {
    font-size: 28px;
    font-weight: bold;
    color: white;
    margin: 0 0 8px 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  .app-tagline {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
  }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

// Login form container
.login-form-container {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

// Form tabs
.login-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  background: #f5f5f5;
  padding: 4px;
  border-radius: 8px;
  
  .tab-btn {
    flex: 1;
    padding: 10px;
    border: none;
    background: transparent;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    color: #666;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &.active {
      background: white;
      color: #667eea;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }
}

// Form groups
.form-group {
  margin-bottom: 20px;
  
  label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #2c3e50;
    margin-bottom: 8px;
    
    .icon {
      font-size: 16px;
    }
  }
  
  input {
    width: 100%;
    padding: 14px 16px;
    border: 2px solid #e0e0e0;
    border-radius: 10px;
    font-size: 15px;
    transition: all 0.3s ease;
    outline: none;
    background: #fafafa;
    
    &:focus {
      border-color: #667eea;
      background: white;
      box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
    }
    
    &::placeholder {
      color: #aaa;
    }
  }
}

// Password input wrapper
.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  
  input {
    padding-right: 48px;
  }
  
  .password-toggle {
    position: absolute;
    right: 12px;
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    
    &:hover {
      opacity: 1;
    }
  }
}

// Form options
.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  
  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #666;
    cursor: pointer;
    user-select: none;
    
    input[type='checkbox'] {
      width: 18px;
      height: 18px;
      cursor: pointer;
      accent-color: #667eea;
    }
  }
  
  .forgot-link {
    font-size: 14px;
    color: #667eea;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: #5568d3;
    }
  }
}

// Submit button
.btn-submit {
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
  
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 0.8s linear infinite;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

// Register link
.register-link {
  text-align: center;
  font-size: 14px;
  color: #666;
  
  .register-link-text {
    color: #667eea;
    font-weight: 600;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: #5568d3;
    }
  }
}

// Third-party login
.third-party-login {
  margin-top: 32px;
  
  .divider {
    display: flex;
    align-items: center;
    text-align: center;
    margin-bottom: 20px;
    
    &::before,
    &::after {
      content: '';
      flex: 1;
      border-bottom: 1px solid #eee;
    }
    
    span {
      padding: 0 12px;
      font-size: 12px;
      color: #999;
    }
  }
  
  .third-party-buttons {
    display: flex;
    justify-content: center;
    gap: 16px;
    
    .tp-btn {
      width: 48px;
      height: 48px;
      border: none;
      border-radius: 12px;
      font-size: 24px;
      cursor: pointer;
      transition: all 0.2s ease;
      background: #f5f5f5;
      
      &:active {
        transform: scale(0.95);
      }
      
      &.tp-wechat { background: #07c160; color: white; }
      &.tp-alipay { background: #1677ff; color: white; }
      &.tp-sms { background: #ff6b6b; color: white; }
    }
  }
}

// Google 登录按钮
.google-login-section {
  margin-top: 20px;
  margin-bottom: 16px;
  
  .google-login-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    padding: 12px 20px;
    background: white;
    border: 2px solid #e0e0e0;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 500;
    color: #333;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover:not(:disabled) {
      border-color: #4285F4;
      background: #f8f9fa;
      box-shadow: 0 2px 8px rgba(66, 133, 244, 0.2);
    }
    
    &:active:not(:disabled) {
      transform: scale(0.98);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    
    svg {
      flex-shrink: 0;
    }
  }
}

// Apple 登录按钮
.apple-login-section {
  margin-top: 20px;
  margin-bottom: 16px;
  
  .apple-login-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    padding: 12px 20px;
    background: #000000;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 500;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover:not(:disabled) {
      background: #1a1a1a;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    }
    
    &:active:not(:disabled) {
      transform: scale(0.98);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    
    svg {
      flex-shrink: 0;
    }
  }
}

// APP 下载
.app-download-section {
  text-align: center;
  margin-top: 20px;
  margin-bottom: 8px;
  
  .app-download-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white !important;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
      text-decoration: none;
    }
    
    &:active {
      transform: translateY(0);
    }
  }
}

// WhatsApp 联系我们
.whatsapp-contact {
  text-align: center;
  margin-top: 16px;
  
  .whatsapp-link {
    display: inline-block;
    padding: 10px 24px;
    background: #25D366;
    color: white !important;
    border-radius: 24px;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    transition: all 0.2s;
    box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);
    
    &:hover {
      background: #1da851;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
}

// Footer
.login-footer {
  position: fixed;
  bottom: 10px;
  left: 0;
  right: 0;
  text-align: center;
  z-index: 999;
  
  p {
    margin: 0;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.8);
    
    a {
      color: rgba(255, 255, 255, 0.9);
      text-decoration: none;
      margin: 0 4px;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
  
  .app-download {
    margin-top: 8px;
    
    .download-link {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 6px 12px;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 16px;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.95);
      transition: all 0.2s ease;
      
      &:hover {
        background: rgba(255, 255, 255, 0.25);
        text-decoration: none;
      }
    }
  }
}

// Terms links in form
.terms-links {
  margin-top: 16px;
  text-align: center;
  
  p {
    margin: 0;
    font-size: 12px;
    color: #999;
    
    a {
      color: #667eea;
      text-decoration: none;
      margin: 0 4px;
      cursor: pointer;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
}

// Toast notification
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%) translateY(-100px);
  padding: 14px 24px;
  border-radius: 10px;
  color: white;
  font-size: 14px;
  z-index: 1000;
  opacity: 0;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  max-width: 90%;
  text-align: center;
  
  &.show {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
  
  &.toast-success { background: #2ecc71; }
  &.toast-error { background: #e74c3c; }
  &.toast-info { background: #3498db; }
}

// Modal
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  
  .modal-content {
    background: white;
    border-radius: 16px;
    width: 100%;
    max-width: 400px;
    max-height: 80vh;
    overflow-y: auto;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  
  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #999;
    padding: 0;
    line-height: 1;
  }
}

.modal-body {
  padding: 20px;
  font-size: 13px;
  line-height: 1.6;
  color: #666;
  
  h4 {
    color: #2c3e50;
    margin: 0 0 12px 0;
    font-size: 14px;
  }
  
  h5 {
    color: #333;
    margin: 12px 0 6px 0;
    font-size: 13px;
  }
  
  p {
    margin: 0 0 12px 0;
  }
}

// 移动端全面优化
@media (max-width: 768px) {
  .login-page {
    padding: 10px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }
  
  .login-content {
    max-width: 100%;
    padding: 25px 20px;
    background: rgba(255, 255, 255, 0.98);
  }
  
  .logo-section {
    margin-bottom: 25px !important;
    
    .logo-icon {
      width: 50px;
      height: 50px;
      font-size: 50px;
      margin-bottom: 10px !important;
    }
    
    .app-name {
      font-size: 24px;
      margin-bottom: 6px !important;
    }
    
    .app-tagline {
      font-size: 13px;
    }
  }
  
  .login-form-container {
    padding: 0;
  }
  
  .login-form {
    .login-tabs {
      margin-bottom: 20px !important;
      
      .tab-btn {
        padding: 10px 15px !important;
        font-size: 14px !important;
      }
    }
    
    .form-group {
      margin-bottom: 18px !important;
      
      label {
        font-size: 14px !important;
        margin-bottom: 6px !important;
        
        .icon {
          font-size: 16px !important;
        }
      }
      
      input,
      select {
        height: 44px !important; // 移动端友好的触摸高度
        font-size: 15px !important;
        padding: 0 12px !important;
        border-radius: 8px !important;
      }
    }
    
    .form-options {
      flex-direction: column;
      gap: 10px;
      align-items: flex-start !important;
      
      .remember-me,
      .forgot-password {
        font-size: 13px !important;
      }
    }
    
    .login-btn {
      height: 48px !important; // 更大的触摸区域
      font-size: 16px !important;
      font-weight: 600 !important;
      border-radius: 10px !important;
    }
  }
  
  .divider {
    margin: 20px 0 !important;
    
    span {
      font-size: 12px !important;
      padding: 0 10px !important;
    }
  }
  
  .quick-login-section {
    .quick-login-title {
      font-size: 13px !important;
      margin-bottom: 12px !important;
    }
    
    .quick-login-buttons {
      gap: 10px !important;
      
      .quick-login-btn {
        padding: 10px 15px !important;
        font-size: 13px !important;
        border-radius: 8px !important;
      }
    }
  }
  
  .footer-text {
    p {
      font-size: 11px !important;
    }
  }
  
  .toast {
    max-width: 85%;
    padding: 12px 18px !important;
    font-size: 13px !important;
    bottom: 20px;
    top: auto;
  }
}
</style>
